import { Injectable, inject } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from "@angular/router";
import { map } from "rxjs/operators";
import { LicenseService } from "./services/common/license.service";
import { AuthenticationService } from "./services/authentication/authentication.service";
import { of } from "rxjs";
import { LICENSE_REQUIRED_PATH, LICENSE_STATUS_PATH } from "./routes/route-paths";

/** Routes that stay reachable while the licence is invalid — they are what tells the user why. */
const ALLOWED_WITHOUT_LICENSE = [LICENSE_STATUS_PATH, LICENSE_REQUIRED_PATH];

/** Strips the leading slash, query string, fragment and matrix params from a router URL. */
function toPath(url: string): string {
  return url.split(/[?#;]/)[0].replace(/^\/+/, "").replace(/\/+$/, "");
}

@Injectable({ providedIn: "root" })
export class LicenseGuard implements CanActivate {
  private license = inject(LicenseService);
  private authenticationService = inject(AuthenticationService);
  private router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    const userId = this.authenticationService.getUserId();

    // No token = user not logged in, or the SSO user is still loading. The next navigation checks the licence.
    if (!userId || !this.authenticationService.hasAuthToken()) {
      return of(true);
    }

    // Note: `state.url` is the URL being navigated to. `this.router.url` is still the *previous*
    // URL while guards run (and "/" on a page reload), so it can never match the target route.
    const targetPath = toPath(state.url);
    if (ALLOWED_WITHOUT_LICENSE.some((allowed) => targetPath === allowed || targetPath.startsWith(`${allowed}/`))) {
      return of(true);
    }

    return this.license.ensureStatus$().pipe(
      map((s) => {
        if (s && !s.valid) {
          console.log(`[License] No valid license found, redirecting to license page.`);

          return this.router.createUrlTree([`/${LICENSE_REQUIRED_PATH}`]);
        }

        return true;
      }),
    );
  }
}
